/**
 * Platform Watch Tracker
 *
 * Earning model:
 *   - Streams are earned by watching content on external platforms
 *     (Instagram, TikTok, YouTube, Facebook).
 *   - When the user taps a platform button they are sent to that app/site.
 *   - When they return, elapsed time is credited at 1 Stream/min (server-side cap: 300/day).
 *   - Zero streams are awarded for time spent inside StreamStack itself.
 */

import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { auth, functions } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { onAuthStateChanged } from 'firebase/auth';
import { Capacitor } from '@capacitor/core';

const WatchContext = createContext(null);

const STORAGE_KEY      = 'ss_watch_departure';
const MAX_WATCH_MINS   = 8 * 60;   // hard cap — 8 hours per single session
const MIN_WATCH_MINS   = 0.5;      // ignore sessions under 30 seconds

export function SessionProvider({ children }) {
  const [activePlatform, setActivePlatform] = useState(null);   // { platform, label, departedAt }
  const [watchResult,    setWatchResult]    = useState(null);   // { platform, label, minutes, credited, capReached }
  const [authedUser,     setAuthedUser]     = useState(undefined);
  const userRef        = useRef(null);
  const creditingRef   = useRef(false);  // prevent double-credit on rapid state changes

  // ── Auth ─────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => {
      userRef.current = u || null;
      setAuthedUser(u || null);
    });
    return unsub;
  }, []);

  // ── Credit helper ─────────────────────────────────────────────────────────────
  const creditWatch = useCallback(async (platform, label, departedAt) => {
    if (creditingRef.current) return;
    creditingRef.current = true;
    localStorage.removeItem(STORAGE_KEY);

    const rawMins   = (Date.now() - departedAt) / 60000;
    const minutes   = Math.min(rawMins, MAX_WATCH_MINS);

    if (minutes < MIN_WATCH_MINS || !userRef.current) {
      setActivePlatform(null);
      creditingRef.current = false;
      return;
    }

    try {
      const { data } = await httpsCallable(functions, 'creditStreams')({ minutes });
      setWatchResult({
        platform,
        label,
        minutes:    Math.round(minutes),
        credited:   data.credited   || 0,
        capReached: data.capReached || false,
      });
    } catch (e) {
      console.error('[WatchCredit]', e);
      setWatchResult({ platform, label, minutes: Math.round(minutes), credited: 0, error: true });
    }

    setActivePlatform(null);
    creditingRef.current = false;
  }, []);

  // ── On app resume: check for a pending departure ──────────────────────────────
  const handleAppResume = useCallback(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return;
    try {
      const { platform, label, departedAt } = JSON.parse(stored);
      creditWatch(platform, label, departedAt);
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [creditWatch]);

  // ── Listen for app returning to foreground ───────────────────────────────────
  useEffect(() => {
    let cleanup = () => {};

    if (Capacitor.isNativePlatform()) {
      let listenerHandle = null;
      import('@capacitor/app').then(({ App }) => {
        App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) handleAppResume();
        }).then(h => { listenerHandle = h; });
      });
      cleanup = () => { listenerHandle?.remove?.(); };
    } else {
      // Web fallback — fires when tab becomes visible again
      const onVis = () => { if (document.visibilityState === 'visible') handleAppResume(); };
      document.addEventListener('visibilitychange', onVis);
      cleanup = () => document.removeEventListener('visibilitychange', onVis);
    }

    return cleanup;
  }, [handleAppResume]);

  // ── Check on mount in case app was killed & reopened while tracking ───────────
  useEffect(() => {
    if (authedUser === undefined) return;   // still loading
    if (authedUser) handleAppResume();
    else            localStorage.removeItem(STORAGE_KEY);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authedUser]);

  // ── Public API ────────────────────────────────────────────────────────────────
  const watchPlatform = useCallback((platform, label, url) => {
    const departure = { platform, label, departedAt: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(departure));
    setActivePlatform(departure);
    setWatchResult(null);
    // '_system' forces the system browser on Android so the app goes to background
    window.open(url, '_system') || (window.location.href = url);
  }, []);

  const clearResult = useCallback(() => setWatchResult(null), []);

  return (
    <WatchContext.Provider value={{ activePlatform, watchPlatform, watchResult, clearResult }}>
      {children}
    </WatchContext.Provider>
  );
}

export function useSession() {
  return useContext(WatchContext);
}
